import React from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router";

import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { ACCOUNT, ORGANIZATIONS } from "src/platform/constants/routes";
import { ALPHA } from "src/shell/styles/designTokens";
import {
  getContainerStyles,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

const UserProfilePage: React.FC = () => {
  const { user } = useLegacyAuth();
  const theme = useTheme();

  if (!user) return null;

  // useLegacyAuth returns either the new auth User (has `name`) or legacy IUser (has `firstName`/`lastName`)
  const displayName =
    "name" in user && user.name
      ? user.name
      : "firstName" in user
        ? `${user.firstName} ${user.lastName}`.trim()
        : "";

  const initial = displayName
    ? displayName.charAt(0).toUpperCase()
    : user.email.charAt(0).toUpperCase();

  const rawCreatedAt =
    "createdAt" in user
      ? (user as { createdAt?: string }).createdAt
      : undefined;
  const memberSince = rawCreatedAt
    ? format(new Date(rawCreatedAt), "MMMM d, yyyy")
    : null;

  const onboardingCompleted =
    "onboardingCompleted" in user
      ? (user as { onboardingCompleted: boolean }).onboardingCompleted
      : false;

  return (
    <Container sx={getContainerStyles(theme, "lg")}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          component={RouterLink}
          to={ORGANIZATIONS.ROOT.path}
          color="inherit"
          underline="hover"
          variant="body2"
        >
          Organizations
        </Link>
        <Typography variant="body2" color="text.primary">
          Profile
        </Typography>
      </Breadcrumbs>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Profile
      </Typography>

      <Stack spacing={3}>
        {/* Identity */}
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <AccountCircleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="subtitle2" sx={sectionLabelSx}>
              Identity
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: theme.palette.primary.main,
                fontSize: "1.5rem",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initial}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {displayName || user.email}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mt: 0.25 }}
              >
                <EmailIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              </Stack>
              {memberSince && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ mt: 0.25 }}
                >
                  <CalendarTodayIcon
                    sx={{ fontSize: 14, color: "text.disabled" }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Member since {memberSince}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>

        {/* Account */}
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <SettingsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="subtitle2" sx={sectionLabelSx}>
              Account
            </Typography>
          </Stack>

          <Stack spacing={0}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 1.25 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Onboarding
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Initial setup and workspace configuration
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.75,
                  bgcolor: onboardingCompleted
                    ? `rgba(${theme.palette.success.main}, ${ALPHA.STAT_TILE_BG})`
                    : undefined,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color={
                    onboardingCompleted ? "success.main" : "text.secondary"
                  }
                >
                  {onboardingCompleted ? "Completed" : "Pending"}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 1.25 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Preferences
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Notifications, security, and appearance
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to={ACCOUNT.SETTINGS.path}
                size="small"
                variant="outlined"
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                Manage
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};

export default UserProfilePage;
